import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useSwipeable } from "react-swipeable";
import SimpleBar from "simplebar-react";

import Carousel from "../components/Carousel";
import CustomiserSwitchList from "../components/CustomiserSwitchList";
import EllipsisLoader from "../components/EllipsisLoader";
import Icon from "../components/Icon";
import Selectable from "../components/Selectable";
import { NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { getPageTitle } from "../resources/routes";
import { Facade, FacadeFinishOption, IconName } from "../types";
import { getPriceFormatted, scrollToTop } from "../util";
import Rendivo from "../util/rendivo";

const ChooseStreetAppealScreen_v2 = observer(() => {
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const [facades, setFacades] = useState<Facade[]>([]);

  const windowSize = useWindowSize();
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const optionsTopRef = useRef<HTMLDivElement>(null);
  const optionsBottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<SimpleBar>(null);
  const [scrollContainerHeight, setScrollContainerHeight] = useState(140); // Value for Mobile
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [selectedOption, setSelectedOption] = useState<FacadeFinishOption | null>(null);

  useEffect(() => {
    scrollToTop();
    // User is able to enter the app directly to this page - need to ensure the footer buttons are correct
    navContext.setFooterModePrimary();
    navContext.resetBackBtnOnClick();

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedFacade(null);
      userContext.resetSelectedFacadeFinishOption();
    });


    if (userContext.selectedHome) {
      setFacades(userContext.selectedHome.facades);
    }

    let selectedFacade = userContext.selectedFacade;
    if (
      !userContext.selectedFacade &&
      userContext.selectedHome &&
      userContext.selectedHome.facades.length
    ) {
      const facade = orderTraditionalFirst(userContext.selectedHome.facades).find(x=>x!==null);
      selectedFacade = facade !== undefined ? facade : null;
    }

    if (selectedFacade) {
      onClickFacade(selectedFacade);
    }
  }, []);

  useEffect(() => {
    // Need to re-calculate the width of the tab scroll
    if (
      windowSize.width > 992 &&
      optionsContainerRef.current &&
      optionsTopRef.current &&
      optionsBottomRef.current &&
      scrollContainerRef.current
    ) {
      scrollContainerRef.current.el.style.maxHeight = "100%";
      const newHeight =
        optionsContainerRef.current.offsetHeight -
        optionsTopRef.current.offsetHeight -
        optionsBottomRef.current.offsetHeight -
        20;
      if (newHeight > 0) {
        setScrollContainerHeight(newHeight);
        scrollContainerRef.current.el.style.maxHeight = newHeight + "px";
      }
    } else {
      setScrollContainerHeight(140);
    }
  }, [
    optionsContainerRef.current,
    optionsTopRef.current,
    optionsBottomRef.current,
    scrollContainerRef.current,
    windowSize.width,
    userContext.selectedFacade,
  ]);

  useEffect(() => {
    navContext.setNextButtonEnabled(
      !!userContext.selectedFacade &&
        !!userContext.selectedFacadeFinishOption.get(
          userContext.selectedFacade.id
        )
    );
    navContext.setSubtotalVisible(userContext.subtotalPrice > 0);
  }, [userContext.selectedFacade, userContext.subtotalPrice]);

  useEffect(() => {
    if(userContext.selectedFacade){
      const option = userContext.selectedFacadeFinishOption.get(userContext.selectedFacade.id);
      if(option){
        setSelectedOption(option);
      }else{
        setSelectedOption(null);
      }
    }else{
      setSelectedOption(null);
    }
  }, [userContext.selectedFacade]);

  const onClickFacade = (facade: Facade) => {
    let currentFinish: FacadeFinishOption | undefined;
    if (userContext.selectedFacade) {
      currentFinish = userContext.selectedFacadeFinishOption.get(
        userContext.selectedFacade.id
      );
    }

    userContext.setSelectedFacade(facade);

    // Search for a finish that matches the current finish's title
    let selectedFinish: FacadeFinishOption | undefined;
    if (currentFinish !== undefined) {
      const matchingFinish = facade.finish_options.filter(
        (item) =>
          currentFinish &&
          item.label.toLowerCase() === currentFinish.label.toLowerCase()
      );

      if (matchingFinish.length) {
        selectedFinish = matchingFinish[0];
        userContext.setSelectedFacadeFinishOption(facade.id, selectedFinish);
      }
    }

    if (!selectedFinish) {
      selectedFinish = userContext.selectedFacadeFinishOption.get(facade.id);
    }
    if (!selectedFinish) {
      const defaultFinished = facade.finish_options.filter(
        (item) => item.default
      );
      if (defaultFinished.length) {
        userContext.setSelectedFacadeFinishOption(
          facade.id,
          defaultFinished[0]
        );
      } else {
        userContext.setSelectedFacadeFinishOption(
          facade.id,
          facade.finish_options[0]
        );
      }
    }
  };

  const onClickFacadeFinishOption = (option: FacadeFinishOption) => {
    if (userContext.selectedFacade) {
      userContext.setSelectedFacadeFinishOption(
        userContext.selectedFacade.id,
        option
      );
      setSelectedOption(option);
    }
  };

  const isFloorplanFlipped =
    !!userContext.selectedHome &&
    !!userContext.isFloorplanFlipped.get(userContext.selectedHome.id);

  const onClickPrev = () => {
    if (canClickPrev()) {
      setActivePage(activePage - 1);
    }
  };
  const onClickNext = () => {
    if (canClickNext()) {
      setActivePage(activePage + 1);
    }
  };

  const canClickPrev = () => {
    return activePage > 1;
  };
  
  const canClickNext = () => {
    return activePage < Math.ceil(facades.length / perPage);
  };

  const swipeHandlers = useSwipeable({
    delta: 10,
    preventDefaultTouchmoveEvent: false,
    trackTouch: true,
    trackMouse: true,
    rotationAngle: 0,
    onSwiped: (eventData) => {
      console.log(eventData);
      if (eventData.dir === "Left") {
        onClickNext();
      } else if (eventData.dir === "Right") {
        onClickPrev();
      }
    },
  });

  useEffect(() => {
    if(windowSize.width > 991){
      setPerPage(8);
    }else if(windowSize.width > 767){
      setPerPage(6);
    }else{
      setPerPage(4);
    }
  }, [windowSize.width]);

  const orderTraditionalFirst = (array: Facade[]) => {
    const index = array.findIndex(({title}) => title.toLowerCase() == "traditional");
    if(index !== -1) {
      //if the matching element is found, 
      const updatedData = [...array];
      //then remove that element and use `unshift`
      updatedData.unshift(...updatedData.splice(index, 1));
      return updatedData;
    }
    return array;
  };

  return (
    <div className={"screen screen-choose-street-appeal"}>
      <div className={"container"}>
        <h2 className="h1"><strong className="number">Step 2:</strong> {getPageTitle(location.pathname)}</h2>
        <p className={"subtitle"}>
          Put your stamp on your new home with a facade option that suits your
          taste.
        </p>
        <p className={"section-label"}>
          Choose your Facade &amp; Facade Finish
        </p>
      </div>
      {userContext.selectedFacade && (
        <div className={"pyht-facade-finish-customiser-v2"}>
          <div
            className={`render-view ${
              !!userContext.selectedHome && userContext.selectedHome.acreage
                ? "acreage"
                : ""
            } ${
              !!userContext.selectedHome && userContext.selectedHome.narrow
                ? "narrow"
                : ""
            } ${
              userContext.selectedFacade.rendivo_levels > 1
                ? "double-story"
                : ""
            }`}
          >
            <div className={"inner"}>
              <EllipsisLoader />
              {userContext.selectedFacade.finish_options.map((option) => {
                return (
                  <img
                    key={(userContext.selectedFacade ? userContext.selectedFacade.id : "") + "_" + option.id}
                    className={
                      selectedOption && selectedOption.id === option.id
                        ? "visible"
                        : "hidden"
                    }
                    src={Rendivo.getFacadeFinishRender(
                      1200,
                      (userContext.selectedFacade ? userContext.selectedFacade.rendivo_id : ""),
                      userContext.isCladdedSelected ? "CLADDED" : null,
                      option.rendivo_id,
                      isFloorplanFlipped
                    )}
                    srcSet={Rendivo.getFacadeFinishRenderSrcset(
                      1200,
                      (userContext.selectedFacade ? userContext.selectedFacade.rendivo_id : ""),
                      userContext.isCladdedSelected ? "CLADDED" : null,
                      option.rendivo_id,
                      isFloorplanFlipped
                    )}
                    sizes="(max-width: 1200px) 100vw, 1200px"
                    alt={""}
                  />
                );
              })}
            </div>
          </div>
          <div
            className={"facade-options-container"}
            key={userContext.selectedFacade.id}
          >
            <div className={"facade-options-inner"} ref={optionsContainerRef}>
              <div className="flex">
                <div className={"options-top"} ref={optionsTopRef}>
                  <div className={"facade-details"}>
                    <div className={"title"}>
                      <h3>{userContext.selectedFacade.title}</h3>
                      <p>
                        {getPriceFormatted(
                          userContext.selectedFacade.base_price
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={"facade-options"}>
                  <div className={"facade-options-list"}>
                    <CustomiserSwitchList
                      items={userContext.selectedFacade.finish_options}
                      selected={selectedOption ? [selectedOption] : []}
                      onClickOption={(option) =>
                        onClickFacadeFinishOption(
                          option as FacadeFinishOption
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={"options-bottom"} ref={optionsBottomRef}>
                <div className="thumbnails-nav">
                  <span className="instructions">Select One</span>
                  <div className="nav-right">
                    <span>{facades.length} Options</span>
                  </div>
                </div>
                <div className={"thumbnails"} {...swipeHandlers}>
                  {orderTraditionalFirst(facades).map((facade, index) => {
                    return (
                      <div
                        key={index}
                        className={`thumbnail ${userContext.selectedFacade && userContext.selectedFacade.id === facade.id ? "active" : ""}`} 
                        onClick={() => onClickFacade(facade)}
                      >
                        <img
                          src={Rendivo.getFacadeFinishRender(
                            300,
                            facade.rendivo_id,
                            null,
                            facade.finish_options[0].rendivo_id,
                            isFloorplanFlipped
                          )}
                          srcSet={Rendivo.getFacadeFinishRenderSrcset(
                            300,
                            facade.rendivo_id,
                            null,
                            facade.finish_options[0].rendivo_id,
                            isFloorplanFlipped
                          )}
                          sizes="(max-width: 300px) 100vw, 300px"
                          alt={""}
                        />
                        <div className="thumbnail-text">{ facade.title }</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ChooseStreetAppealScreen_v2;
